import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import {
  Container,
  Label,
  Button,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import { Row, Col, Table } from "reactstrap";
import ApiCaller from "../../../../../api/apiCaller";
import CustomDropdownInput from "../../../../../components/CustomDropdownInput";
import CustomTextInput from "../../../../../components/CustomTextInput";
import "./index.css";

interface SelectBuildingInputProps {
  selectBuildingCallback?: (buildingLocationId: number) => void;
}

const CreateBuilding = ({
  selectBuildingCallback,
}: SelectBuildingInputProps) => {
  const [currentUser, setCurrentUser] = useState<any>({});

  const [buildingData, setBuildingData] = useState<any>({
    name: "",
    address_number: "",
    street_name: "",
  });
  const [cityOptions, setCityOptions] = useState<Array<any>>([]);
  const [districtOptions, setDistrictOptions] = useState<Array<any>>([]);
  const [wardOptions, setWardOptions] = useState<Array<any>>([]);
  const [locationTypeOptions, setLocationTypeOptions] = useState<Array<any>>(
    []
  );

  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(
    null
  );
  const [selectedWardId, setSelectedWardId] = useState<number | null>(null);
  const [
    selectedLocationTypeOptions,
    setSelectedLocationTypeOptions,
  ] = useState<number | null>(null);
  const [isCreatingNewBuilding, setIsCreatingNewBuilding] = useState<boolean>(
    false
  );

  const updateAttr = (attribute: string, value: any) => {
    const updatedBuildingData = buildingData;
    if (["city", "district", "ward", "location_type"].includes(attribute)) {
      updatedBuildingData[attribute] = {
        id: value.value,
      };
      if (attribute === "city") {
        setSelectedCityId(updatedBuildingData.city.id);
      }
      if (attribute === "district") {
        setSelectedDistrictId(updatedBuildingData.district.id);
      }
      if (attribute === "ward") {
        setSelectedWardId(updatedBuildingData.ward.id);
      }
      if (attribute == "location_type") {
        setSelectedLocationTypeOptions(updatedBuildingData.location_type.id);
      }
    } else {
      updatedBuildingData[attribute] = value;
    }
    setBuildingData({ ...updatedBuildingData });
  };

  const getCurrentUser = async () => {
    const res = await ApiCaller.get("userme/me");
    if (res.result) {
      setCurrentUser(res.result);
    }
  };

  const saveBuilding = async () => {
    setIsCreatingNewBuilding(true);
    const res = await ApiCaller.post("location/", {
      ...buildingData,
      created_by: {
        id: currentUser.id,
      },
      changed_by: {
        id: currentUser.id,
      },
    });
    if (res.id) {
      const buildingLocationId = res.id;
      if (selectBuildingCallback) {
        selectBuildingCallback(buildingLocationId);
      }
    }
    setIsCreatingNewBuilding(false);
  };

  /* Load district, city and ward */
  const getCityOptions = async () => {
    const results: Array<any> = [];
    const res = await ApiCaller.get(`city/`);
    if (res?.result) {
      res?.result?.forEach((item: any) => {
        results.push({
          value: item.id,
          label: item.name,
        });
      });
      setCityOptions(results);
      setDistrictOptions([]);
    }
  };
  const getDistrictOptions = async () => {
    const results: Array<any> = [];
    let districtFilterString = `(col:city,opr:rel_o_m,value:${selectedCityId})`;
    const res = await ApiCaller.get(
      `district/?q=(filters:!(${districtFilterString}))`
    );
    if (res?.result) {
      res?.result?.forEach((item: any) => {
        results.push({
          value: item.id,
          label: item.name,
        });
      });
      setDistrictOptions(results);
      setWardOptions([]);
    }
  };
  const getWardOptions = async () => {
    const results: Array<any> = [];
    let wardFilterString = `(col:district,opr:rel_o_m,value:${selectedDistrictId})`;
    const res = await ApiCaller.get(`ward/?q=(filters:!(${wardFilterString}))`);
    if (res?.result) {
      res?.result?.forEach((item: any) => {
        results.push({
          value: item.id,
          label: item.name,
        });
      });
      setWardOptions(results);
    }
  };
  const getLocationTypeOptions = async () => {
    const results: Array<any> = [];
    const res = await ApiCaller.get(`location_type/`);
    if (res?.result) {
      res?.result?.forEach((item: any) => {
        results.push({
          value: item.id,
          label: item.name,
        });
      });
      setLocationTypeOptions(results);
    }
  };

  useEffect(() => {
    getCityOptions();
    getLocationTypeOptions();
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (selectedCityId !== null) {
      getDistrictOptions();
    }
  }, [selectedCityId]);

  useEffect(() => {
    if (selectedDistrictId !== null) {
      getWardOptions();
    }
  }, [selectedDistrictId]);

  return (
    <Row>
      <Col xs={12} sm={6}>
        <CustomTextInput
          callback={updateAttr}
          data={buildingData}
          labelText="Tên tòa nhà"
          attributeName="name"
        />
      </Col>
      <Col xs={12} sm={6}>
        <CustomDropdownInput
          callback={updateAttr}
          data={buildingData}
          options={locationTypeOptions}
          labelText="Loại tòa nhà"
          attributeName="location_type"
        />
      </Col>
      <Col xs={12} sm={6}>
        <CustomDropdownInput
          callback={updateAttr}
          data={buildingData}
          options={cityOptions}
          labelText="Thành phố"
          attributeName="city"
        />
      </Col>
      <Col xs={12} sm={6}>
        <CustomDropdownInput
          callback={updateAttr}
          data={buildingData}
          options={districtOptions}
          labelText="Quận"
          attributeName="district"
        />
      </Col>
      <Col xs={12} sm={6}>
        <CustomDropdownInput
          callback={updateAttr}
          data={buildingData}
          options={wardOptions}
          labelText="Phường"
          attributeName="ward"
        />
      </Col>
      <Col xs={12} sm={6}>
        <CustomTextInput
          callback={updateAttr}
          data={buildingData}
          labelText="Số nhà"
          attributeName="address_number"
        />
      </Col>
      <Col xs={12} sm={6}>
        <CustomTextInput
          callback={updateAttr}
          data={buildingData}
          labelText="Tên đường"
          attributeName="street_name"
        />
      </Col>
      <Col xs={12}>
        <Button
          disabled={isCreatingNewBuilding}
          color="success"
          onClick={saveBuilding}
        >
          {isCreatingNewBuilding ? "Đang lưu..." : "Lưu tòa nhà"}
        </Button>
      </Col>
    </Row>
  );
};

export default CreateBuilding;
